/**
 * WMM2025 — magnetic variation (declination) from the World Magnetic Model.
 *
 * Source of the coefficients: NOAA/NCEI + BGS World Magnetic Model 2025,
 * file `WMM.COF` out of `WMM2025COF.zip`
 * (https://www.ncei.noaa.gov/products/world-magnetic-model), header line
 * `2025.0            WMM-2025        11/13/2024`. The model is a work of the
 * U.S. Government and is in the public domain; no attribution is required, but
 * the provenance is kept here so the table can be diffed against NOAA's file
 * when WMM2030 replaces it.
 *
 * The implementation is the standard degree-12 spherical-harmonic expansion in
 * Schmidt semi-normalised associated Legendre functions, with the geodetic →
 * geocentric conversion and the back-rotation of the field vector, exactly as
 * described in the WMM technical report. No third-party code.
 *
 * Sign convention used everywhere in @avia/core (see calc/wind.ts):
 * **east variation positive, west negative** — magnetic = true − variation,
 * so mwSK = TC + WCA − VAR. That is also how the LBA Flugdurchführungsplan is
 * filled in ("-2" for 2° W, as in the 1987 booklet example).
 */
/** Model epoch (decimal year). */
export const WMM_EPOCH = 2025.0;
/** The model is only valid inside [WMM_EPOCH, WMM_VALID_UNTIL). */
export const WMM_VALID_UNTIL = 2030.0;
/** Highest spherical-harmonic degree in WMM2025. */
const N_MAX = 12;
/** Number of (n,m) slots for n ≤ N_MAX, indexed as n(n+1)/2 + m. */
const N_TERMS = ((N_MAX + 1) * (N_MAX + 2)) / 2;
/**
 * WMM2025 Gauss coefficients, verbatim rows of NOAA's `WMM.COF`:
 * `[n, m, g (nT), h (nT), ġ (nT/yr), ḣ (nT/yr)]`.
 * Rows are in the file's own order (n = 1…12, m = 0…n); the file's signed
 * zeros are written as plain `0`.
 */
const WMM2025_COF = [
    [1, 0, -29351.8, 0, 12, 0],
    [1, 1, -1410.8, 4545.4, 9.7, -21.5],
    [2, 0, -2556.6, 0, -11.6, 0],
    [2, 1, 2951.1, -3133.6, -5.2, -27.7],
    [2, 2, 1649.3, -815.1, -8, -12.1],
    [3, 0, 1361, 0, -1.3, 0],
    [3, 1, -2404.1, -56.6, -4.2, 4],
    [3, 2, 1243.8, 237.5, 0.4, -0.3],
    [3, 3, 453.6, -549.5, -15.6, -4.1],
    [4, 0, 895, 0, -1.6, 0],
    [4, 1, 799.5, 278.6, -2.4, -1.1],
    [4, 2, 55.7, -133.9, -6, 4.1],
    [4, 3, -281.1, 212, 5.6, 1.6],
    [4, 4, 12.1, -375.6, -7, -4.4],
    [5, 0, -233.2, 0, 0.6, 0],
    [5, 1, 368.9, 45.4, 1.4, -0.5],
    [5, 2, 187.2, 220.2, 0, 2.2],
    [5, 3, -138.7, -122.9, 0.6, 0.4],
    [5, 4, -142, 43, 2.2, 1.7],
    [5, 5, 20.9, 106.1, 0.9, 1.9],
    [6, 0, 64.4, 0, -0.2, 0],
    [6, 1, 63.8, -18.4, -0.4, 0.3],
    [6, 2, 76.9, 16.8, 0.9, -1.6],
    [6, 3, -115.7, 48.8, 1.2, -0.4],
    [6, 4, -40.9, -59.8, -0.9, 0.9],
    [6, 5, 14.9, 10.9, 0.3, 0.7],
    [6, 6, -60.7, 72.7, 0.9, 0.9],
    [7, 0, 79.5, 0, 0, 0],
    [7, 1, -77, -48.9, -0.1, 0.6],
    [7, 2, -8.8, -14.4, -0.1, 0.5],
    [7, 3, 59.3, -1, 0.5, -0.8],
    [7, 4, 15.8, 23.4, -0.1, 0],
    [7, 5, 2.5, -7.4, -0.8, -1],
    [7, 6, -11.1, -25.1, -0.8, 0.6],
    [7, 7, 14.2, -2.3, 0.8, -0.2],
    [8, 0, 23.2, 0, -0.1, 0],
    [8, 1, 10.8, 7.1, 0.2, -0.2],
    [8, 2, -17.5, -12.6, 0, 0.5],
    [8, 3, 2, 11.4, 0.5, -0.4],
    [8, 4, -21.7, -9.7, -0.1, 0.4],
    [8, 5, 16.9, 12.7, 0.3, -0.5],
    [8, 6, 15, 0.7, 0.2, -0.6],
    [8, 7, -16.8, -5.2, 0, 0.3],
    [8, 8, 0.9, 3.9, 0.2, 0.2],
    [9, 0, 4.6, 0, 0, 0],
    [9, 1, 7.8, -24.8, -0.1, -0.3],
    [9, 2, 3, 12.2, 0.1, 0.3],
    [9, 3, -0.2, 8.3, 0.3, -0.3],
    [9, 4, -2.5, -3.3, -0.3, 0.3],
    [9, 5, -13.1, -5.2, 0, 0.2],
    [9, 6, 2.4, 7.2, 0.3, -0.1],
    [9, 7, 8.6, -0.6, -0.1, -0.2],
    [9, 8, -8.7, 0.8, 0.1, 0.4],
    [9, 9, -12.9, 10, -0.1, 0.1],
    [10, 0, -1.3, 0, 0.1, 0],
    [10, 1, -6.4, 3.3, 0, 0],
    [10, 2, 0.2, 0, 0.1, 0],
    [10, 3, 2, 2.4, 0.1, -0.2],
    [10, 4, -1, 5.3, 0, 0.1],
    [10, 5, -0.6, -9.1, -0.3, -0.1],
    [10, 6, -0.9, 0.4, 0, 0.1],
    [10, 7, 1.5, -4.2, -0.1, 0],
    [10, 8, 0.9, -3.8, -0.1, -0.1],
    [10, 9, -2.7, 0.9, 0, 0.2],
    [10, 10, -3.9, -9.1, 0, 0],
    [11, 0, 2.9, 0, 0, 0],
    [11, 1, -1.5, 0, 0, 0],
    [11, 2, -2.5, 2.9, 0, 0.1],
    [11, 3, 2.4, -0.6, 0, 0],
    [11, 4, -0.6, 0.2, 0, 0.1],
    [11, 5, -0.1, 0.5, -0.1, 0],
    [11, 6, -0.6, -0.3, 0, 0],
    [11, 7, -0.1, -1.2, 0, 0.1],
    [11, 8, 1.1, -1.7, -0.1, 0],
    [11, 9, -1, -2.9, -0.1, 0],
    [11, 10, -0.2, -1.8, -0.1, 0],
    [11, 11, 2.6, -2.3, -0.1, 0],
    [12, 0, -2, 0, 0, 0],
    [12, 1, -0.2, -1.3, 0, 0],
    [12, 2, 0.3, 0.7, 0, 0],
    [12, 3, 1.2, 1, 0, -0.1],
    [12, 4, -1.3, -1.4, 0, 0.1],
    [12, 5, 0.6, 0, 0, 0],
    [12, 6, 0.6, 0.6, 0.1, 0],
    [12, 7, 0.5, -0.1, 0, 0],
    [12, 8, -0.1, 0.8, 0, 0],
    [12, 9, -0.4, 0.1, 0, 0],
    [12, 10, -0.2, -1, -0.1, 0],
    [12, 11, -1.3, 0.1, 0, 0],
    [12, 12, -0.7, 0.2, -0.1, -0.1],
];
const G = new Float64Array(N_TERMS);
const H = new Float64Array(N_TERMS);
const G_DOT = new Float64Array(N_TERMS);
const H_DOT = new Float64Array(N_TERMS);
for (const [n, m, g, h, gd, hd] of WMM2025_COF) {
    const i = (n * (n + 1)) / 2 + m;
    G[i] = g;
    H[i] = h;
    G_DOT[i] = gd;
    H_DOT[i] = hd;
}
/** Bounds-safe read. Every index used below is in range by construction. */
function at(a, i) {
    return a[i] ?? 0;
}
const RAD = Math.PI / 180;
// WGS-84 reference ellipsoid and the WMM's geomagnetic reference radius (km).
const WGS84_A = 6378.137;
const WGS84_F = 1 / 298.257223563;
const WGS84_E2 = WGS84_F * (2 - WGS84_F);
const GEOMAG_R = 6371.2;
/**
 * Decimal year in NOAA's convention: whole year plus the elapsed fraction of
 * that year, UTC. 2025-01-01T00:00Z → 2025.0; 2027-07-02T12:00Z → 2027.5.
 */
export function decimalYear(date) {
    const y = date.getUTCFullYear();
    const start = Date.UTC(y, 0, 1);
    const next = Date.UTC(y + 1, 0, 1);
    return y + (date.getTime() - start) / (next - start);
}
/** True while WMM2025 is in its published validity span. */
export function isWithinWmmValidity(date) {
    const t = decimalYear(date);
    return t >= WMM_EPOCH && t < WMM_VALID_UNTIL;
}
/**
 * Schmidt semi-normalised associated Legendre functions P(n,m)(x) and their
 * derivatives with respect to geocentric latitude, for x = sin(latitude).
 * Gauss-normalised recursion first, then scaled — the low-degree route, which
 * is what WMM's own code takes for n ≤ 16.
 */
function legendre(x) {
    const p = new Float64Array(N_TERMS);
    const dp = new Float64Array(N_TERMS);
    const norm = new Float64Array(N_TERMS);
    const z = Math.sqrt((1 - x) * (1 + x)); // cos(latitude)
    p[0] = 1;
    dp[0] = 0;
    for (let n = 1; n <= N_MAX; n++) {
        for (let m = 0; m <= n; m++) {
            const i = (n * (n + 1)) / 2 + m;
            if (n === m) {
                const j = ((n - 1) * n) / 2 + m - 1;
                p[i] = z * at(p, j);
                dp[i] = z * at(dp, j) + x * at(p, j);
            }
            else if (n === 1 && m === 0) {
                p[i] = x * at(p, 0);
                dp[i] = x * at(dp, 0) - z * at(p, 0);
            }
            else if (m > n - 2) {
                const j = ((n - 1) * n) / 2 + m;
                p[i] = x * at(p, j);
                dp[i] = x * at(dp, j) - z * at(p, j);
            }
            else {
                const j = ((n - 1) * n) / 2 + m;
                const k = ((n - 2) * (n - 1)) / 2 + m;
                const c = ((n - 1) * (n - 1) - m * m) / ((2 * n - 1) * (2 * n - 3));
                p[i] = x * at(p, j) - c * at(p, k);
                dp[i] = x * at(dp, j) - z * at(p, j) - c * at(dp, k);
            }
        }
    }
    // Gauss-normalised → Schmidt quasi-normalised scale factors.
    norm[0] = 1;
    for (let n = 1; n <= N_MAX; n++) {
        const i0 = (n * (n + 1)) / 2;
        norm[i0] = (at(norm, ((n - 1) * n) / 2) * (2 * n - 1)) / n;
        for (let m = 1; m <= n; m++) {
            const i = i0 + m;
            norm[i] = at(norm, i - 1) * Math.sqrt(((n - m + 1) * (m === 1 ? 2 : 1)) / (n + m));
        }
    }
    for (let n = 1; n <= N_MAX; n++) {
        for (let m = 0; m <= n; m++) {
            const i = (n * (n + 1)) / 2 + m;
            p[i] = at(p, i) * at(norm, i);
            // negated: the derivative is with respect to latitude, not co-latitude
            dp[i] = -at(dp, i) * at(norm, i);
        }
    }
    return { p, dp };
}
/**
 * Magnetic variation (declination) at a place and time, in degrees,
 * **east positive / west negative** — the sign @avia/core's `magneticHeading`
 * and the LBA form's VAR field expect.
 *
 * Outside the model's validity span (2025.0–2030.0) the secular variation is
 * simply extrapolated; use `isWithinWmmValidity` if the caller wants to warn.
 */
export function magneticDeclination(p, date) {
    const dt = decimalYear(date) - WMM_EPOCH;
    const h = p.altKm ?? 0;
    // The By/cos(lat) term is singular exactly at the poles, where declination is
    // meaningless anyway; step just off the pole rather than special-casing it.
    const latDeg = Math.min(89.999999, Math.max(-89.999999, p.lat));
    const lonRad = p.lon * RAD;
    const latRad = latDeg * RAD;
    // geodetic → geocentric spherical
    const sinLat = Math.sin(latRad);
    const cosLat = Math.cos(latRad);
    const rc = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);
    const xp = (rc + h) * cosLat;
    const zp = (rc * (1 - WGS84_E2) + h) * sinLat;
    const r = Math.sqrt(xp * xp + zp * zp);
    const latGc = Math.asin(zp / r);
    const { p: leg, dp: dLeg } = legendre(Math.sin(latGc));
    const cosML = new Float64Array(N_MAX + 1);
    const sinML = new Float64Array(N_MAX + 1);
    cosML[0] = 1;
    sinML[0] = 0;
    for (let m = 1; m <= N_MAX; m++) {
        cosML[m] = Math.cos(m * lonRad);
        sinML[m] = Math.sin(m * lonRad);
    }
    const ratio = GEOMAG_R / r;
    let pow = ratio * ratio; // (a/r)^2, stepped to (a/r)^(n+2) inside the loop
    let bx = 0;
    let by = 0;
    let bz = 0;
    for (let n = 1; n <= N_MAX; n++) {
        pow *= ratio;
        for (let m = 0; m <= n; m++) {
            const i = (n * (n + 1)) / 2 + m;
            const g = at(G, i) + dt * at(G_DOT, i);
            const hc = at(H, i) + dt * at(H_DOT, i);
            const c = at(cosML, m);
            const s = at(sinML, m);
            const gh = g * c + hc * s;
            bz -= pow * gh * (n + 1) * at(leg, i);
            by += pow * (g * s - hc * c) * m * at(leg, i);
            bx -= pow * gh * at(dLeg, i);
        }
    }
    by /= Math.cos(latGc);
    // rotate the geocentric vector back into the geodetic frame
    const psi = latGc - latRad;
    const bxGeo = bx * Math.cos(psi) - bz * Math.sin(psi);
    return Math.atan2(by, bxGeo) / RAD;
}
/**
 * The LBA form's VAR field: whole degrees, east positive, west with a leading
 * minus — `4.6 → "5"`, `-2.4 → "-2"`, `0.3 → "0"` (never "-0").
 * Non-finite input yields the empty string, as elsewhere in core's formatters.
 */
export function formatVar(decl) {
    if (!Number.isFinite(decl))
        return '';
    const d = Math.round(decl);
    return d === 0 ? '0' : String(d);
}
//# sourceMappingURL=wmm.js.map